import React, { useEffect, useRef } from "react";

// Interactive 3D scene for the hero: a wireframe icosahedron core with
// pulsing orbital nodes, three rotating rings, ambient particles, and
// mouse-parallax response. All animation is handled inside a Three.js
// render loop and cleaned up on unmount.
export default function Hero3DScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let animId;
    let renderer;
    let mount = mountRef.current;

    const waitFor = (predicate, timeout = 8000) =>
      new Promise((resolve, reject) => {
        const s = Date.now();
        const tick = () => {
          if (predicate()) return resolve();
          if (Date.now() - s > timeout) return reject(new Error("timeout"));
          setTimeout(tick, 60);
        };
        tick();
      });

    (async () => {
      try {
        await waitFor(() => window.THREE);
      } catch (e) {
        return;
      }
      if (cancelled || !mount) return;
      const THREE = window.THREE;

      const width = mount.clientWidth || 400;
      const height = mount.clientHeight || 400;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
      camera.position.z = 6.2;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      mount.appendChild(renderer.domElement);

      // Pivot group -- everything is nested inside so future positional
      // tweaks are contained. The object stays centered in the canvas.
      const pivot = new THREE.Group();
      pivot.position.x = 0;
      scene.add(pivot);

      // === Core icosahedron (wireframe) ===
      const coreGeo = new THREE.IcosahedronGeometry(1.78, 1);
      const coreMat = new THREE.MeshBasicMaterial({
        color: 0x38bdf8,
        wireframe: true,
        transparent: true,
        opacity: 0.9,
      });
      const core = new THREE.Mesh(coreGeo, coreMat);
      pivot.add(core);

      // Inner soft fill for depth
      const innerGeo = new THREE.IcosahedronGeometry(1.6, 1);
      const innerMat = new THREE.MeshBasicMaterial({
        color: 0x0ea5e9,
        transparent: true,
        opacity: 0.12,
      });
      const inner = new THREE.Mesh(innerGeo, innerMat);
      pivot.add(inner);

      // Outer wireframe shell
      const outerGeo = new THREE.IcosahedronGeometry(2.12, 0);
      const outerMat = new THREE.MeshBasicMaterial({
        color: 0x7dd3fc,
        wireframe: true,
        transparent: true,
        opacity: 0.25,
      });
      const outer = new THREE.Mesh(outerGeo, outerMat);
      pivot.add(outer);

      // === Orbital rings ===
      const rings = [];
      for (let i = 0; i < 3; i++) {
        const ringGeo = new THREE.TorusGeometry(2.85 + i * 0.32, 0.008, 8, 96);
        const ringMat = new THREE.MeshBasicMaterial({
          color: 0x38bdf8,
          transparent: true,
          opacity: 0.35 - i * 0.08,
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.x = (Math.PI / 3) * (i + 1);
        ring.rotation.y = (Math.PI / 4) * i;
        pivot.add(ring);
        rings.push(ring);
      }

      // === Orbital nodes ===
      const nodes = [];
      const nodeCount = 7;
      for (let i = 0; i < nodeCount; i++) {
        const nodeGeo = new THREE.SphereGeometry(0.09, 20, 20);
        const nodeMat = new THREE.MeshBasicMaterial({
          color: 0x67e8f9,
          transparent: true,
          opacity: 0.95,
        });
        const node = new THREE.Mesh(nodeGeo, nodeMat);
        const angle = (i / nodeCount) * Math.PI * 2;
        const radius = 2.95 + (i % 2) * 0.18;
        node.userData = { angle, radius, phase: i * 0.7, orbit: 0.35 + (i % 3) * 0.08 };
        pivot.add(node);
        nodes.push(node);

        // Node halo
        const haloGeo = new THREE.SphereGeometry(0.18, 20, 20);
        const haloMat = new THREE.MeshBasicMaterial({
          color: 0x38bdf8,
          transparent: true,
          opacity: 0.2,
        });
        const halo = new THREE.Mesh(haloGeo, haloMat);
        node.add(halo);
      }

      // === Connection lines from core to nodes ===
      const linesGroup = new THREE.Group();
      pivot.add(linesGroup);
      const lineMat = new THREE.LineBasicMaterial({
        color: 0x38bdf8,
        transparent: true,
        opacity: 0.18,
      });
      nodes.forEach(() => {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute(
          "position",
          new THREE.BufferAttribute(new Float32Array(6), 3)
        );
        const line = new THREE.Line(geo, lineMat);
        linesGroup.add(line);
      });

      // === Ambient particles ===
      const partCount = 260;
      const partGeo = new THREE.BufferGeometry();
      const positions = new Float32Array(partCount * 3);
      for (let i = 0; i < partCount * 3; i++) {
        positions[i] = (Math.random() - 0.5) * 12;
      }
      partGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const partMat = new THREE.PointsMaterial({
        size: 0.028,
        color: 0x67e8f9,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const particles = new THREE.Points(partGeo, partMat);
      pivot.add(particles);

      // === Interaction ===
      let mouseX = 0, mouseY = 0;
      let targetX = 0, targetY = 0;
      const onPointer = (e) => {
        const rect = mount.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        targetX = (cx / rect.width) * 2 - 1;
        targetY = -((cy / rect.height) * 2 - 1);
      };
      window.addEventListener("mousemove", onPointer);
      const onLeave = () => { targetX = 0; targetY = 0; };
      mount.addEventListener("mouseleave", onLeave);

      let t = 0;
      const animate = () => {
        animId = requestAnimationFrame(animate);
        t += 0.008;

        // Ease mouse
        mouseX += (targetX - mouseX) * 0.06;
        mouseY += (targetY - mouseY) * 0.06;

        // Core rotations
        core.rotation.x += 0.0035;
        core.rotation.y += 0.0055;
        inner.rotation.x -= 0.002;
        inner.rotation.y += 0.003;
        outer.rotation.x += 0.001;
        outer.rotation.y -= 0.0018;

        // Ring spin
        rings.forEach((r, i) => {
          r.rotation.z += 0.0018 + i * 0.0006;
          r.rotation.x += 0.0004 * (i + 1);
        });

        // Orbital nodes
        nodes.forEach((n, i) => {
          const a = n.userData.angle + t * n.userData.orbit;
          const r = n.userData.radius;
          n.position.x = Math.cos(a) * r;
          n.position.z = Math.sin(a) * r;
          n.position.y = Math.sin(a * 2 + n.userData.phase) * 0.55;
          const s = 1 + Math.sin(t * 3 + i) * 0.18;
          n.scale.set(s, s, s);
        });

        // Update connection lines
        linesGroup.children.forEach((line, i) => {
          const n = nodes[i];
          if (!n) return;
          const arr = line.geometry.attributes.position.array;
          arr[0] = 0; arr[1] = 0; arr[2] = 0;
          arr[3] = n.position.x;
          arr[4] = n.position.y;
          arr[5] = n.position.z;
          line.geometry.attributes.position.needsUpdate = true;
        });

        // Particle drift
        particles.rotation.y += 0.0006;
        particles.rotation.x += 0.0003;

        // Parallax on the pivot (rotates around the object's visible center)
        pivot.rotation.y += (mouseX * 0.4 - pivot.rotation.y) * 0.05;
        pivot.rotation.x += (mouseY * 0.25 - pivot.rotation.x) * 0.05;

        renderer.render(scene, camera);
      };
      animate();

      const onResize = () => {
        if (!mount) return;
        const w = mount.clientWidth;
        const h = mount.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      };
      window.addEventListener("resize", onResize);

      // cleanup captured in outer closure
      mount.__cleanup = () => {
        cancelAnimationFrame(animId);
        window.removeEventListener("resize", onResize);
        window.removeEventListener("mousemove", onPointer);
        if (mount) mount.removeEventListener("mouseleave", onLeave);
        if (renderer && renderer.domElement && renderer.domElement.parentNode === mount) {
          mount.removeChild(renderer.domElement);
        }
        if (renderer) renderer.dispose();
      };
    })();

    return () => {
      cancelled = true;
      if (mount && mount.__cleanup) mount.__cleanup();
    };
  }, []);

  return <div ref={mountRef} className="w-full h-full" aria-hidden="true" />;
}
